<template>
	<div class="mkt-month-year-input" ref="monthYearInput">
		<md-field :class="{ 'md-invalid': hasErrors }" class="mkt-date-input">
			<mkt-label
				v-if="txLabel"
				class="mkt-field__label"
				:txLabel="txLabel"
				:bl-required="blRequired"
				:txObservation="txObservation"
			></mkt-label>
			<md-input
				placeholder="mm/aaaa"
				:required="blRequired"
				:id="txId"
				v-model="strMonthYear"
				@blur="validateTextField"
				@focus="closePicker()"
				v-on:keypress="validateInputType"
			>
			</md-input>
			<mkt-icon
				class="mkt-date-input__icon-clear-date"
				v-if="!blRequired && !blReadOnly"
				tx-icon="icon-delete"
				@mkt-icon:icon-clicked="clearDate()"
				tx-icon-color="#6f6f6f"
				tx-font-size="18px"
			/>
			<mkt-icon
				class="mkt-date-input__icon-calendar"
				v-if="!blReadOnly"
				tx-icon="icon-calendar"
				@mkt-icon:icon-clicked="togglePicker()"
				tx-icon-color="#003C86"
				tx-font-size="20px"
			/>
			<span class="md-error" v-if="hasErrors">{{
				formValidationError || 'Campo obrigatório'
			}}</span>
		</md-field>
		<div class="month-picker-container">
			<month-picker
				v-show="showPicker"
				v-model="selectedDate"
				@close="closePicker()"
				:defaultMonth="value.month ? value.month : null"
				:defaultYear="value.year ? value.year : new Date().getFullYear()"
				:min-date="calculateMinDate()"
				:max-date="calculateMaxDate()"
				:no-default="true"
				lang="pt"
			/>
		</div>
	</div>
</template>

<script>
import PropConfig from 'pojos/PropConfig'
import { MonthPicker } from 'vue-month-picker'
import InputType from 'domains/InputType'
import { REGEX_NOT_PURE_NUMBER } from 'constants/Regex'
import { isComponentOutOfScreen } from 'src/services/componentService'

export default {
	name: 'mkt-month-year-input',
	components: {
		MonthPicker
	},
	props: {
		txLabel: new PropConfig({ type: String }),
		blRequired: new PropConfig({ type: Boolean, valueDefault: false }),
		blReadOnly: new PropConfig({
			type: Boolean,
			required: false,
			valueDefault: false
		}),
		txId: new PropConfig({ type: String, required: false }),
		txObservation: new PropConfig({ type: String, required: false }),
		value: {
			month: null,
			year: null
		},
		minDate: {
			month: null,
			year: null
		},
		maxDate: {
			month: null,
			year: null
		}
	},
	data() {
		return {
			hasErrors: false,
			formValidationError: '',
			showPicker: false,
			selectedDate: null,
			InputType
		}
	},
	computed: {
		strMonthYear() {
			if (this.value) {
				const month = this.value.month
				const year = this.value.year
				if (month && year) {
					return this.formatDate(month, year)
				}
			}
			return ''
		}
	},
	methods: {
		validateTextField(e) {
			this.clearError()

			const input = e.target
			const value = input.value

			const month = Number(value.split('/')[0])
			const year = Number(value.split('/')[1])

			if (!month || !year) {
				if (this.blRequired) {
					this.setError('Campo obrigatório')
				}
				return
			}

			if (month < 1 || month > 12) {
				this.setError('Mês deve estar entre 1 e 12')
				return
			}

			if (this.minDate) {
				const minMonth = this.minDate.month
				const minYear = this.minDate.year

				if (year < minYear) {
					this.setError(`A data mínima é ${this.formatDate(minMonth, minYear)}`)
					return
				}

				if (year === minYear && month < minMonth) {
					this.setError(`A data mínima é ${this.formatDate(minMonth, minYear)}`)
					return
				}
			}

			if (this.maxDate) {
				const maxMonth = this.maxDate.month
				const maxYear = this.maxDate.year

				if (year > maxYear) {
					this.setError(`A data máxima é ${this.formatDate(maxMonth, maxYear)}`)
					return
				}

				if (year === maxYear && month > maxMonth) {
					this.setError(`A data máxima é ${this.formatDate(maxMonth, maxYear)}`)
					return
				}
			}

			this.selectedDate = {
				month: month,
				year: year
			}
		},
		validateInputType(e) {
			const isNotValid = e.key.match(REGEX_NOT_PURE_NUMBER)

			if (isNotValid) {
				e.preventDefault()
			}

			const input = e.target
			const valueOnlyNumbers = input.value.replaceAll('/', '')
			const contentLength = valueOnlyNumbers.length

			if (contentLength === 4) {
				input.value =
					valueOnlyNumbers.slice(0, 1) + '/' + valueOnlyNumbers.slice(1, 7)
			} else if (contentLength === 5) {
				input.value =
					valueOnlyNumbers.slice(0, 2) + '/' + valueOnlyNumbers.slice(2, 7)
			} else if (contentLength === 6) {
				input.value = ''
			}
		},
		calculateMinDate() {
			const day = 1
			const month = this.minDate.month - 1 // (em Date, os meses começam em 0)
			const year = this.minDate.year
			return new Date(year, month, day)
		},
		calculateMaxDate() {
			const day = 1
			const month = this.maxDate.month - 1 // (em Date, os meses começam em 0)
			const year = this.maxDate.year
			return new Date(year, month, day)
		},
		togglePicker() {
			this.showPicker = !this.showPicker
			if (this.isPickerVisible()) {
				this.$nextTick(() => {
					const inputRect = this.$refs.monthYearInput.getBoundingClientRect()
					const picker = this.$el.querySelector('.month-picker-container')

					if (isComponentOutOfScreen(inputRect, picker)) {
						const newPosition = inputRect.width - picker.offsetWidth
						picker.style.left = `${newPosition}px`
						return
					}

					picker.style.left = '80%'
				})
			}
		},
		closePicker() {
			this.showPicker = false
		},
		openPicker() {
			this.showPicker = true
		},
		isPickerVisible() {
			return this.showPicker
		},
		handleClickOutside(event) {
			const monthYearInput = this.$refs.monthYearInput
			if (monthYearInput && !monthYearInput.contains(event.target)) {
				this.closePicker()
			}
		},
		clearDate() {
			this.$emit('input', '')
		},
		formatDate(month, year) {
			return `${month < 10 ? '0' + month : month}/${year}`
		},
		setError(message) {
			this.hasErrors = true
			this.formValidationError = message
			this.clearDate()
		},
		clearError() {
			this.hasErrors = false
			this.formValidationError = ''
		},
		updateValue(month, year) {
			const newValue = {
				month: month,
				year: year
			}
			this.$emit('input', newValue)
		},
		isPickerOutOfScreen() {}
	},
	mounted() {
		document.addEventListener('click', this.handleClickOutside)
	},
	beforeDestroy() {
		document.removeEventListener('click', this.handleClickOutside)
	},
	watch: {
		selectedDate(newDate) {
			if (newDate) {
				const month = newDate.monthIndex || newDate.month
				const year = newDate.year

				this.clearError()
				this.updateValue(month, year)
				this.closePicker()
			}
		}
	}
}
</script>

<style src="./mktMonthYearInput.scss" lang="scss"></style>
